// PrintPreviewModal.jsx

import React, { useCallback, useMemo, useRef } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import { getEnumOptionLabel } from 'utilities/EnumOptions';
import { normalizeSchemaFieldType, isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { formatMeasurementValue } from 'utilities/Measurement';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getFieldLabel } from 'utilities/NodeLabels';
import { getNodeSubtitle, isSubtitleFieldType } from 'utilities/NodeSubtitle';
import { getResolvedFields } from 'utilities/NodeType';
import { openPrintDocument } from 'utilities/Print';
import { formatTemporalFieldValue, isTemporalFieldType } from 'views/utils/temporalFieldFormat';

// CSS
import './PrintPreviewModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

const NON_RENDERED_FIELD_TYPES = new Set(['activity', 'comments', 'cover']);

const PRINT_DATE_OPTIONS = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
};

const PRINT_DATETIME_OPTIONS = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
};

const PRINT_PREVIEW_DOCUMENT_STYLES = `
    * {
        box-sizing: border-box;
    }

    html,
    body {
        background: #ffffff;
        color: #000000;
        font-family: Arial, sans-serif;
        height: auto;
        margin: 0;
        min-height: 0;
        overflow: visible;
        padding: 0;
    }

    .print-preview-modal-container,
    .print-preview-modal-sheet,
    .print-preview-modal-fields {
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
    }

    .print-preview-modal-sheet {
        background: #ffffff;
        color: #000000;
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin: 0 auto;
        max-width: 820px;
        padding: 32px;
    }

    .print-preview-modal-actions {
        display: none !important;
    }

    .print-preview-modal-header {
        border-bottom: 1px solid #000000;
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-bottom: 20px;
    }

    .print-preview-modal-caption,
    .print-preview-modal-field-label {
        color: #000000;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .print-preview-modal-title {
        color: #000000;
        font-size: 32px;
        font-weight: 700;
        line-height: 1.2;
        word-break: break-word;
    }

    .print-preview-modal-fields {
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    .print-preview-modal-field-row {
        display: flex;
        flex-direction: column;
        gap: 8px;
        page-break-inside: avoid;
    }

    .print-preview-modal-field-value,
    .print-preview-modal-empty-state {
        border: 1px solid #000000;
        color: #000000;
        font-size: 16px;
        line-height: 1.5;
        min-height: 52px;
        padding: 14px 16px;
        white-space: pre-wrap;
        word-break: break-word;
    }

    @page {
        margin: 0.5in;
    }
`;

const stringifyFallbackValue = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const getObjectDisplayLabel = (value) => {
    if (!value || typeof value !== 'object') return stringifyFallbackValue(value);

    const preferredLabel = value.titleFull
        || value.title
        || value.name
        || value.label
        || value.email
        || value.value
        || value.key;

    if (preferredLabel != null && String(preferredLabel).trim()) {
        return String(preferredLabel);
    }

    return stringifyFallbackValue(value);
};

const formatListDisplayValue = (value, { fieldType = '' } = {}) => {
    const items = Array.isArray(value) ? value : value ? [value] : [];
    if (items.length === 0) return '';

    const labels = items
        .map((item) => {
            if (typeof item === 'object') {
                return getObjectDisplayLabel(item);
            }

            return stringifyFallbackValue(item);
        })
        .filter(Boolean);

    return labels.join('\n');
};

const readPrintableFieldValue = (node, field, nodeTypeKey) => {
    const fieldType = normalizeSchemaFieldType(field?.type);

    if (isSubtitleFieldType(fieldType)) return getNodeSubtitle(node);
    if (fieldType === 'tags') return node?.tags ?? [];

    return readNodeFieldRawValue(node, field, nodeTypeKey);
};

const formatPrintableFieldValue = ({ field, rawValue, language }) => {
    const fieldType = normalizeSchemaFieldType(field?.type);

    if (rawValue == null || rawValue === '') return '';

    if (fieldType === 'boolean') return rawValue ? 'Yes' : 'No';

    if (fieldType === 'money') {
        if (rawValue == null || rawValue === '') return '';
        return formatPrice(rawValue, resolveFieldCurrencyCode(field));
    }

    if (isEnumCompatibleFieldType(fieldType)) {
        return getEnumOptionLabel(field?.options || [], rawValue, language);
    }

    if (fieldType === 'number') {
        const numericValue = Number(rawValue);
        return Number.isFinite(numericValue) ? numericValue.toLocaleString() : String(rawValue);
    }

    if (fieldType === 'measurement') {
        return formatMeasurementValue(rawValue);
    }

    if (fieldType === 'percentage') {
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue)) return '';
        const clamped = Math.max(0, Math.min(100, Math.round(numericValue)));
        return `${clamped}%`;
    }

    if (isTemporalFieldType(fieldType)) {
        return formatTemporalFieldValue({
            value: rawValue,
            fieldType,
            locale: 'en-US',
            dateOptions: PRINT_DATE_OPTIONS,
            dateTimeOptions: PRINT_DATETIME_OPTIONS,
        }) || '';
    }

    if (fieldType === 'location' || fieldType === 'profile') {
        return getObjectDisplayLabel(rawValue);
    }

    if (fieldType === 'checklist') {
        return '';
    }

    if (
        fieldType === 'relation'
        || fieldType === 'people'
        || fieldType === 'tags'
    ) {
        return formatListDisplayValue(rawValue, { fieldType });
    }

    if (Array.isArray(rawValue)) {
        return formatListDisplayValue(rawValue, { fieldType });
    }

    if (typeof rawValue === 'object') {
        return getObjectDisplayLabel(rawValue);
    }

    return String(rawValue);
};

/**
 * PrintPreviewModal
 *
 * Renders a print-styled preview for a node title and its string schema fields.
 *
 * @param {boolean} isOpen - Whether the preview modal is open.
 * @param {object} node - The node whose printable fields should be displayed.
 * @param {function} onClose - Called when the modal should close.
 *
 * @returns {ReactNode} The rendered component.
 */
const PrintPreviewModal = ({ isOpen, node, onClose }) => {

    // ----- CONTEXT ----- //

    const language = useCurrentLanguage();
    const { getRevision } = useNodeTypes();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- REFS ----- //

    const sheetRef = useRef(null);

    const nodeTypesRevision = getRevision();

    // ----- MEMOS ----- //

    const printableTitle = useMemo(() => {
        const title = typeof node?.title === 'string' ? node.title.trim() : '';
        return title || 'Untitled';
    }, [node?.title]);

    const printableFields = useMemo(() => {
        void nodeTypesRevision;

        const nodeTypeKey = typeof node?.type === 'string' ? node.type.trim() : '';
        if (!nodeTypeKey) return [];

        return getResolvedFields(nodeTypeKey, { includeHidden: false, excludeSecured: true })
            .filter((field) => field?.name && !NON_RENDERED_FIELD_TYPES.has(normalizeSchemaFieldType(field?.type)))
            .map((field) => {
                const rawValue = readPrintableFieldValue(node, field, nodeTypeKey);
                const formattedValue = formatPrintableFieldValue({
                    field,
                    rawValue,
                    language,
                });

                return {
                    label: getFieldLabel(field, language) || field.name,
                    name: field.name,
                    value: formattedValue,
                };
            });
    }, [language, node, nodeTypesRevision]);

    // ----- HANDLERS ----- //

    /**
     * Handles print.
     */

    const handlePrint = useCallback(() => {
        if (typeof window === 'undefined') return;

        const printableMarkup = sheetRef.current?.outerHTML;
        if (!printableMarkup) return;

        openPrintDocument({
            bodyMarkup: printableMarkup,
            styles: PRINT_PREVIEW_DOCUMENT_STYLES,
            title: printableTitle,
        });
    }, [printableTitle]);

    // ----- RENDER ----- //

    return (
        <Modal
          isOpen={isOpen}
          width={isMobile ? '100%' : 760}
          maxHeight="85vh"
          padding="0"
          onClose={onClose}
        >
            <ModalForm>
                <div className="print-preview-modal-container">

                    {/* SHEET */}
                    <div className="print-preview-modal-sheet" ref={sheetRef}>

                        {/* HEADER */}
                        <div className="print-preview-modal-header">

                            {/* CAPTION */}
                            <div className="print-preview-modal-caption">
                                Print Preview
                            </div>

                            {/* TITLE */}
                            <div className="print-preview-modal-title">
                                {printableTitle}
                            </div>
                        </div>

                        {/* FIELDS */}
                        <div className="print-preview-modal-fields">
                            {printableFields.length === 0 ? (

                                <div className="print-preview-modal-empty-state">
                                    No printable schema fields are available yet.
                                </div>
                            ) : (
                                printableFields.map((field) => (

                                    <div className="print-preview-modal-field-row" key={field.name}>

                                        {/* FIELD LABEL */}
                                        <div className="print-preview-modal-field-label">
                                            {field.label}
                                        </div>

                                        {/* FIELD VALUE */}
                                        <div className="print-preview-modal-field-value">
                                            {field.value || ' '}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </ModalForm>

            <ModalButtons>
                <Button
                  backgroundColor="#ffffff"
                  borderColor="#000000"
                  foregroundColor="#000000"
                  hoverBackgroundColor="#f3f3f3"
                  hoverForegroundColor="#000000"
                  label="Print"
                  onClick={handlePrint}
                />
            </ModalButtons>
        </Modal>
    );
};

// Dev Tools
PrintPreviewModal.displayName = 'PrintPreviewModal';

export default PrintPreviewModal;
